import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Eye, EyeOff, KeyRound, Loader2, Lock, ShieldAlert } from 'lucide-react';
import { getApiUrl } from '../utils/api';

type Props = {
  isOpen: boolean;
  userId: string | null;
  userEmail: string;
  onClose: () => void;
  onSuccess: () => void;
};

function validatePasswordStrength(password: string): string[] {
  const errors: string[] = [];
  if (password.length < 8) errors.push('minLength');
  if (!/[A-Z]/.test(password)) errors.push('uppercase');
  if (!/[a-z]/.test(password)) errors.push('lowercase');
  if (!/[0-9]/.test(password)) errors.push('digit');
  if (!/[^A-Za-z0-9]/.test(password)) errors.push('special');
  return errors;
}

export function ConfirmHardPasswordResetModal({
  isOpen,
  userId,
  userEmail,
  onClose,
  onSuccess,
}: Props) {
  const { t } = useTranslation();
  const [step, setStep] = useState<1 | 2>(1);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setIsLoading(false);
    setError(null);
    setErrorDetails([]);
  }, [isOpen, userId]);

  if (!isOpen || !userId) return null;
  if (typeof document === 'undefined') return null;

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  const handleConfirmReset = async () => {
    setError(null);
    setErrorDetails([]);

    const strengthErrors = validatePasswordStrength(password);
    if (strengthErrors.length > 0) {
      setError(t('users.passwordReset.invalidPassword'));
      setErrorDetails(strengthErrors.map((key) => t(`users.passwordReset.rules.${key}`)));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('users.passwordReset.mismatch'));
      return;
    }

    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/admin/users/${userId}/password`), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        setError(data.message || t('users.passwordReset.error'));
        if (Array.isArray(data.errors)) {
          setErrorDetails(
            data.errors.map((err: unknown) =>
              typeof err === 'string' ? err : JSON.stringify(err)
            )
          );
        }
        return;
      }
      onSuccess();
    } catch {
      setError(t('users.passwordReset.networkError'));
    } finally {
      setIsLoading(false);
    }
  };

  const modal = (
    <div className={`fixed inset-0 z-[9999] overflow-y-auto ${isLoading ? 'cursor-wait' : ''}`}>
      <div className="flex min-h-screen items-center justify-center px-4 py-8">
        <div
          className="fixed inset-0 bg-gray-900/60"
          onClick={isLoading ? undefined : handleClose}
        />
        <div className="relative w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-2xl">
          {step === 1 ? (
            <>
              <div className="bg-amber-50 px-6 py-5 border-b border-amber-100">
                <div className="flex items-start gap-3">
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
                    <ShieldAlert className="h-6 w-6 text-amber-700" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t('users.passwordReset.step1Title')}
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      {t('users.passwordReset.step1Subtitle')}
                    </p>
                  </div>
                </div>
              </div>
              <div className="px-6 py-5 space-y-4">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    {t('users.passwordReset.target')}
                  </p>
                  <p className="mt-1 font-medium text-gray-900">{userEmail}</p>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <p className="text-sm text-amber-900">
                    {t('users.passwordReset.step1Warning')}
                  </p>
                  <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-amber-900">
                    <li>{t('users.passwordReset.consequenceReplace')}</li>
                    <li>{t('users.passwordReset.consequenceSessions')}</li>
                    <li>{t('users.passwordReset.consequenceNotify')}</li>
                  </ul>
                </div>
              </div>
              <div className="flex justify-end gap-3 bg-gray-50 px-6 py-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {t('users.passwordReset.cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700"
                >
                  {t('users.passwordReset.continue')}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="bg-red-50 px-6 py-5 border-b border-red-100">
                <div className="flex items-start gap-3">
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
                    <AlertTriangle className="h-6 w-6 text-red-700" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t('users.passwordReset.step2Title')}
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      {t('users.passwordReset.step2Subtitle', { email: userEmail })}
                    </p>
                  </div>
                </div>
              </div>
              <div className="px-6 py-5 space-y-4">
                {error && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    {error}
                    {errorDetails.length > 0 && (
                      <ul className="mt-2 list-disc space-y-1 pl-5">
                        {errorDetails.map((detail) => (
                          <li key={detail}>{detail}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    {t('users.passwordReset.newPassword')}
                  </label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="input-field w-full pl-10 pr-10"
                      autoComplete="new-password"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-3"
                      onClick={() => setShowPassword((prev) => !prev)}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">{t('users.passwordReset.requirements')}</p>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    {t('users.passwordReset.confirmPassword')}
                  </label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="input-field w-full pl-10"
                      autoComplete="new-password"
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 bg-gray-50 px-6 py-4">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isLoading}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {t('users.passwordReset.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleConfirmReset}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 rounded-lg bg-red-700 px-4 py-2 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-50"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('users.passwordReset.submitting')}
                    </>
                  ) : (
                    <>
                      <KeyRound className="h-4 w-4" />
                      {t('users.passwordReset.confirm')}
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
